<template>
  <div class="billing-tab" data-test="billing-tab">
    <!-- Tab header — same eyebrow / display title / lede block as the Team and Credits tabs (.ma-tabhead). -->
    <div class="ma-tabhead">
      <div class="ma-tabhead__eyebrow">Billing</div>
      <h3 class="ma-tabhead__title">Your plan and payments</h3>
      <p class="ma-tabhead__lede">
        What you pay Teamio, the card we charge, and your invoices.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bt-state" data-test="billing-loading">
      <b-spinner small class="mr-2" /> Loading your billing details…
    </div>

    <!-- Could not load -->
    <b-card v-else-if="loadError" class="bt-card" data-test="billing-error">
      <p class="bt-error mb-3"><i class="la la-exclamation-circle mr-2"></i>{{ loadError }}</p>
      <b-button variant="outline-dark" size="sm" data-test="billing-retry" @click="load">Try again</b-button>
    </b-card>

    <!-- No billing account yet -->
    <b-card v-else-if="state === 'no-billing'" class="bt-card" data-test="billing-none">
      <h4 class="bt-card__title mb-2"><i class="la la-receipt mr-2"></i>Billing isn&rsquo;t set up here yet</h4>
      <p class="text-muted mb-0">
        We don&rsquo;t have a billing account on file for your practice in this screen yet.
        Please contact Teamio support and we&rsquo;ll get it sorted out.
      </p>
    </b-card>

    <template v-else>
      <!-- Expired / expiring card -->
      <b-alert v-if="banner" show :variant="banner.variant" class="bt-banner" data-test="billing-card-banner">
        <i class="la la-exclamation-triangle mr-2"></i>{{ banner.text }}
      </b-alert>

      <!-- Action errors (could not open Stripe) -->
      <b-alert :show="!!actionError" variant="danger" class="bt-banner" data-test="billing-action-error">
        {{ actionError }}
      </b-alert>

      <b-row>
        <!-- ======================== Plan ======================== -->
        <b-col cols="12" lg="7" class="mb-4">
          <b-card class="h-100 bt-card" data-test="billing-plan-card">
            <h4 class="bt-card__title mb-3"><i class="la la-layer-group mr-2"></i>Your plan</h4>

            <p v-if="state === 'no-plan'" class="text-muted mb-0" data-test="billing-no-plan">
              There&rsquo;s no active plan on your account right now. If that doesn&rsquo;t look right,
              please contact Teamio support.
            </p>

            <div v-for="(plan, pi) in summary.plans" :key="pi" class="bt-plan" data-test="billing-plan">
              <div class="bt-plan__status" :class="'bt-tone--' + planStatusOf(plan).tone">{{ planStatusOf(plan).label }}</div>
              <ul class="bt-items">
                <li v-for="(item, ii) in plan.items" :key="ii" class="bt-item" data-test="billing-plan-item">
                  <span class="bt-item__name">
                    {{ item.name }}<span v-if="item.quantity > 1" class="bt-item__qty"> × {{ item.quantity }}</span>
                  </span>
                  <span class="bt-item__price">
                    {{ money(item.amountCents, item.currency) }}<span v-if="interval(item.interval)" class="bt-item__per"> / {{ interval(item.interval) }}</span>
                  </span>
                </li>
              </ul>
              <div v-if="plan.nextCharge && plan.nextCharge.date" class="bt-next" data-test="billing-next-charge">
                <span class="bt-next__label">Next payment</span>
                <span class="bt-next__value">
                  <strong v-if="plan.nextCharge.amountCents !== null">{{ money(plan.nextCharge.amountCents, plan.nextCharge.currency) }}</strong>
                  <span v-if="plan.nextCharge.amountCents !== null"> on </span>{{ date(plan.nextCharge.date) }}
                </span>
              </div>
            </div>

            <p class="bt-note mt-3 mb-0">
              To change your plan, or if anything here doesn&rsquo;t look right, please contact Teamio support.
            </p>
          </b-card>
        </b-col>

        <!-- ======================== Payment method + billing details ======================== -->
        <b-col cols="12" lg="5" class="mb-4">
          <b-card class="h-100 bt-card" data-test="billing-card-card">
            <h4 class="bt-card__title mb-3"><i class="la la-credit-card mr-2"></i>Payment method</h4>

            <div v-if="summary.card" class="bt-card-line" data-test="billing-card">
              <div class="bt-card-line__label">{{ cardText }}</div>
              <div class="bt-card-line__meta">
                Expires {{ cardExpiryText }}
                <span v-if="summary.card.status === 'expired'" class="bt-chip bt-tone--bad">Expired</span>
                <span v-else-if="summary.card.status === 'expiring'" class="bt-chip bt-tone--warn">Expiring soon</span>
              </div>
            </div>
            <p v-else class="text-muted" data-test="billing-no-card">No card on file yet.</p>

            <b-button
              variant="primary"
              class="mt-2 bt-btn"
              :disabled="!!opening"
              data-test="billing-update-card"
              @click="open('payment_method_update')"
            >
              <b-spinner v-if="opening === 'payment_method_update'" small class="mr-1" />
              {{ opening === 'payment_method_update' ? 'Opening…' : (summary.card ? 'Update card' : 'Add a card') }}
            </b-button>
            <p class="bt-note mt-2 mb-0">You&rsquo;ll be taken to a secure page to enter your card, then brought back here.</p>

            <hr class="bt-rule" />

            <h5 class="bt-sub">Billing details</h5>
            <p class="text-muted mb-2">Your practice name, billing address, phone and tax ID on your invoices.</p>
            <b-button
              variant="outline-dark"
              class="bt-btn"
              :disabled="!!opening"
              data-test="billing-update-details"
              @click="open('customer_update')"
            >
              <b-spinner v-if="opening === 'customer_update'" small class="mr-1" />
              {{ opening === 'customer_update' ? 'Opening…' : 'Update billing details' }}
            </b-button>
          </b-card>
        </b-col>
      </b-row>

      <!-- ======================== Invoices ======================== -->
      <b-card class="bt-card mb-4" data-test="billing-invoices-card">
        <h4 class="bt-card__title mb-3"><i class="la la-file-invoice-dollar mr-2"></i>Invoices</h4>

        <p v-if="!summary.invoices.length" class="text-muted mb-0" data-test="billing-no-invoices">
          No invoices yet. They&rsquo;ll appear here after your first payment.
        </p>

        <ul v-else class="bt-invoices" data-test="billing-invoices">
          <li v-for="(inv, i) in summary.invoices" :key="i" class="bt-inv" data-test="billing-invoice">
            <div class="bt-inv__main">
              <div class="bt-inv__date">{{ date(inv.date) }}</div>
              <div class="bt-inv__meta">
                <span class="bt-inv__what">{{ inv.label }}</span><span v-if="inv.number" class="bt-inv__num"> · {{ inv.number }}</span>
              </div>
            </div>
            <div class="bt-inv__amount">{{ money(inv.amountCents, inv.currency) }}</div>
            <div class="bt-inv__status">
              <span class="bt-chip" :class="'bt-tone--' + invoiceStatusOf(inv.status).tone">{{ invoiceStatusOf(inv.status).label }}</span>
            </div>
            <div class="bt-inv__links">
              <a v-if="inv.hostedUrl" :href="inv.hostedUrl" target="_blank" rel="noopener noreferrer" data-test="billing-invoice-view">View</a>
              <a v-if="inv.pdfUrl" :href="inv.pdfUrl" target="_blank" rel="noopener noreferrer" data-test="billing-invoice-pdf">PDF</a>
            </div>
          </li>
        </ul>
      </b-card>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { fetchBillingSummary, openBillingPortal, billingReturnUrl } from '@/api/billing';
import {
  formatMoney, intervalLabel, formatBillingDate, cardLabel, cardExpiry, cardBanner,
  billingState, invoiceStatus, planStatus, billingErrorMessage,
} from '@/utils/billing/billingView';

/**
 * My Account → Billing (phase 1): see the plan and what it costs, the card on file, the invoices; update the card and
 * the billing details. Admin / Super Admin only — MyAccount.vue renders this ONLY for them (v-if + lazy), and the server
 * enforces it again (403), so a non-admin never even triggers a request.
 *
 * NO cancel and NO plan change here, on purpose (Saumil, 2026-10-14). The buttons open Stripe's secure hosted pages.
 * Design: docs/plans/2026-10-14-billing-tab-design.md. Wording lives in utils/billing/billingView.js (tested).
 */
export default {
  name: 'BillingTab',

  data() {
    return {
      loading: true,
      loadError: null,
      summary: { hasBilling: false, plans: [], card: null, invoices: [] },
      opening: null, // 'payment_method_update' | 'customer_update' while a button is working
      actionError: null,
    };
  },

  computed: {
    ...mapState('customer', ['siteID', 'settings']),

    state() { return billingState(this.summary); },
    banner() { return cardBanner(this.summary.card); },
    cardText() { return cardLabel(this.summary.card); },
    cardExpiryText() { return cardExpiry(this.summary.card); },

    // Site timezone (GENERAL_TIMEZONE) — dates are the practice's day, not the browser's.
    timezone() {
      const s = (this.settings || []).find((x) => x && x.Property === 'GENERAL_TIMEZONE');
      return (s && s.PropertyValue) || null;
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    money: formatMoney,
    interval: intervalLabel,
    planStatusOf: (plan) => planStatus(plan && plan.status),
    invoiceStatusOf: invoiceStatus,
    date(iso) { return formatBillingDate(iso, this.timezone); },

    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        const data = await fetchBillingSummary({ auth: this.$store.state.getAuth(), siteID: this.siteID });
        this.summary = {
          hasBilling: !!(data && data.hasBilling),
          plans: (data && data.plans) || [],
          card: (data && data.card) || null,
          invoices: (data && data.invoices) || [],
        };
      } catch (err) {
        this.loadError = billingErrorMessage(err);
      } finally {
        this.loading = false;
      }
    },

    // Opens one page of Stripe's hosted portal. On success the browser leaves for Stripe and returns to this tab.
    async open(flow) {
      if (this.opening) return;
      this.actionError = null;
      this.opening = flow;
      try {
        const url = await openBillingPortal({ auth: this.$store.state.getAuth(), siteID: this.siteID, flow, returnUrl: billingReturnUrl() });
        window.location.href = url;
      } catch (err) {
        this.actionError = billingErrorMessage(err);
        this.opening = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* The shared My Account tab design language (same tokens as Credits / Patient Payments). */
@import './PatientPayments/tokens';

.billing-tab { max-width: 960px; }

/* TAB HEADER — scoped copy of the .ma-tabhead block the Team and Credits tabs declare (24px). */
.ma-tabhead { margin-bottom: 20px; }
.ma-tabhead__eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: $pp-brand; margin-bottom: 8px;
}
.ma-tabhead__title {
  margin: 0; font-family: $pp-font-display; font-weight: 800; font-size: 24px;
  letter-spacing: -0.03em; line-height: 1.05; color: $pp-text;
}
.ma-tabhead__lede { margin: 8px 0 0; font-size: 14px; line-height: 1.5; color: $pp-text-muted; max-width: 660px; }

.bt-state { padding: 1.5rem 0; color: $pp-text-muted; }
.bt-error { color: $pp-red-text; margin: 0; }
.bt-banner { border-radius: $pp-radius-sm; }

.bt-card { background: $pp-surface; border: 1px solid $pp-border; border-radius: $pp-radius; box-shadow: $pp-shadow; }
.bt-card__title { font-size: 1rem; font-weight: 700; color: $pp-text-strong; margin: 0; }
.bt-card__title .la { color: $pp-brand; }
.bt-sub { font-size: 0.9rem; font-weight: 700; color: $pp-text-strong; margin: 0 0 4px; }
.bt-note { font-size: 12.5px; color: $pp-text-muted; line-height: 1.45; }
.bt-rule { border-top: 1px solid $pp-border; margin: 1.25rem 0 1rem; }

/* The primary action uses the shared brand colour (the same one Credits' Save buttons use). */
.bt-btn.btn-primary { background: $pp-brand; border-color: $pp-brand; }
.bt-btn.btn-primary:hover:not(:disabled) { background: $pp-brand-deep; border-color: $pp-brand-deep; }

/* ---- plan ---- */
.bt-plan + .bt-plan { margin-top: 1.25rem; padding-top: 1.25rem; border-top: 1px solid $pp-border; }
.bt-plan__status { display: inline-block; font-size: 12px; font-weight: 700; margin-bottom: 8px; }
.bt-items { list-style: none; margin: 0; padding: 0; }
.bt-item {
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  padding: 9px 0; border-bottom: 1px solid $pp-border;
}
.bt-item:last-child { border-bottom: 0; }
.bt-item__name { color: $pp-text-strong; font-weight: 600; min-width: 0; overflow-wrap: anywhere; }
.bt-item__qty { color: $pp-text-muted; font-weight: 500; }
.bt-item__price { color: $pp-text-strong; font-weight: 700; white-space: nowrap; }
.bt-item__per { color: $pp-text-muted; font-weight: 500; }
.bt-next {
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px; flex-wrap: wrap;
  margin-top: 12px; padding: 10px 12px; background: $pp-surface-sunken; border-radius: $pp-radius-sm;
}
.bt-next__label { color: $pp-text-muted; font-size: 13px; }
.bt-next__value { color: $pp-text-strong; }

/* ---- card ---- */
.bt-card-line { padding: 10px 12px; background: $pp-surface-sunken; border-radius: $pp-radius-sm; margin-bottom: 8px; }
.bt-card-line__label { font-weight: 700; color: $pp-text-strong; }
.bt-card-line__meta { font-size: 13px; color: $pp-text-muted; margin-top: 2px; }

/* ---- chips + tones (never colour alone: every chip carries a word) ---- */
.bt-chip {
  display: inline-block; padding: 2px 10px; border-radius: $pp-radius-pill; font-size: 12px; font-weight: 700;
  background: $pp-surface-sunken; color: $pp-text-muted; margin-left: 6px;
}
.bt-tone--ok   { color: $pp-brand; }
.bt-tone--warn { color: $pp-amber-text; }
.bt-tone--bad  { color: $pp-red-text; }
.bt-chip.bt-tone--ok   { background: $pp-brand-subtle; border: 1px solid $pp-brand-border; }
.bt-chip.bt-tone--warn { background: $pp-amber-subtle; border: 1px solid $pp-amber-border; }
.bt-chip.bt-tone--bad  { background: $pp-red-subtle;   border: 1px solid $pp-red-border; }

/* ---- invoices: a list of rows that wraps cleanly from phone to desktop ---- */
.bt-invoices { list-style: none; margin: 0; padding: 0; }
.bt-inv {
  display: grid; grid-template-columns: minmax(0, 1fr) auto auto auto; align-items: center; gap: 8px 16px;
  padding: 12px 0; border-bottom: 1px solid $pp-border;
}
.bt-inv:last-child { border-bottom: 0; }
.bt-inv__date { font-weight: 700; color: $pp-text-strong; }
.bt-inv__meta { font-size: 13px; color: $pp-text-muted; overflow-wrap: anywhere; }
.bt-inv__amount { font-weight: 700; color: $pp-text-strong; text-align: right; white-space: nowrap; }
.bt-inv__status .bt-chip { margin-left: 0; }
.bt-inv__links { display: flex; gap: 12px; white-space: nowrap; }
.bt-inv__links a { color: $pp-brand; font-weight: 600; }
.bt-inv__links a:hover { color: $pp-brand-deep; }

@media (max-width: 575.98px) {
  .bt-inv { grid-template-columns: minmax(0, 1fr) auto; }
  .bt-inv__status { grid-column: 1; }
  .bt-inv__links { grid-column: 2; justify-content: flex-end; }
}
</style>
